/**
 * Call lifecycle state machine — frontend side of one ParallelCall.
 *
 * Voir docs/refactor/call-state-machine.md pour le diagramme complet, les
 * invariants, et la liste des events qui doivent y être routés.
 *
 * Une machine par call. Le pool est orchestré par DialerSessionContext qui
 * écoute les transitions vers `completed` pour relancer.
 *
 * Phase 2 du refacto : SQUELETTE. Les actions/guards sont des stubs qui se
 * contentent de logguer. La logique réelle (toasts, sons, fetch enrichi,
 * hangup Twilio) sera branchée en Phase 4 étape par étape.
 */

import { setup, assign } from 'xstate';

// ─── Types publics ───────────────────────────────────────────────────────────

export type VerdictSource = 'amd' | 'stt' | 'heuristic';

export interface EnrichedContact {
  callId: string;
  prospect_id?: string;
  prospect_name?: string;
  prospect_phone?: string;
  prospect_company?: string;
  prospect_email?: string;
  prospect_title?: string;
  linkedin_url?: string;
  website?: string;
  notes?: string;
  // Champs Twilio brut (avant enrichissement BDD)
  prospectName?: string;
  prospectPhone?: string;
  prospectCompany?: string;
  // Flags posés par les transitions
  callEnded?: boolean;
  callStartedAt?: number;
}

export interface CallMachineContext {
  callId: string;
  prospectId: string;
  phone: string;
  enrichedContact: EnrichedContact | null;
  bridgedAt: number | null;
  verdictSource: VerdictSource | null;
  /** Outcome posé par l'agent au clic "Valider et continuer" — null avant qualif. */
  outcome: string | null;
  /** Notes saisies par l'agent dans la modale. */
  notes: string;
}

export type CallMachineEvent =
  | { type: 'DIAL_START'; prospectId: string; phone: string; callId: string }
  | { type: 'TWILIO_RINGING' }
  | { type: 'TWILIO_ANSWERED'; bridgedAt: number }
  | { type: 'VOICEMAIL_DETECTED'; transcript?: string }
  | { type: 'HUMAN_CONFIRMED'; verdictSource: VerdictSource }
  | { type: 'HANGUP_REMOTE'; durationMs: number }
  | { type: 'HANGUP_AGENT' }
  | { type: 'QUALIFICATION_SAVED'; outcome: string; notes: string }
  | { type: 'SKIP_QUALIFICATION' }
  | { type: 'STOP_ALL' }
  | { type: 'ENRICH_CONTACT'; enrichedContact: EnrichedContact };

export type CallMachineInput = {
  callId: string;
  prospectId: string;
  phone: string;
  /** Contact pré-rempli depuis les Twilio custom params, sera enrichi via ENRICH_CONTACT. */
  initialContact?: EnrichedContact;
};

// ─── Stubs (à brancher en Phase 4) ───────────────────────────────────────────
//
// Toutes les actions/guards sont déclarées ici en stub. Quand on migrera les
// useEffect du DialerContext, on viendra remplacer chaque stub par sa vraie
// implémentation. Avantage : la structure de la machine est validable par tsc
// avant que la logique ne soit branchée.

const stubAction = (label: string) => () => {
  // eslint-disable-next-line no-console
  console.log(`[callMachine][stub-action] ${label}`);
};

const stubGuard = (label: string) => () => {
  // eslint-disable-next-line no-console
  console.log(`[callMachine][stub-guard] ${label} → true`);
  return true;
};

// ─── Machine ─────────────────────────────────────────────────────────────────

export const callMachine = setup({
  types: {
    context: {} as CallMachineContext,
    events: {} as CallMachineEvent,
    input: {} as CallMachineInput,
  },
  guards: {
    /** Le pool n'a aucun autre call en bridging|connected_to_agent. */
    isPoolFree: stubGuard('isPoolFree'),
    /** Aucun STOP_ALL récent (<3s) côté DialerSessionContext. */
    agentNotStopRequested: stubGuard('agentNotStopRequested'),
    /** bridgedAt + 11_000ms < now — heuristique humain. */
    hasMinimumDuration: stubGuard('hasMinimumDuration'),
    /** AMD webhook a tranché human avec haute confiance. */
    amdIsConfirmedHuman: stubGuard('amdIsConfirmedHuman'),
  },
  actions: {
    playDialBeep:         stubAction('playDialBeep'),
    playConnectionBeep:   stubAction('playConnectionBeep'),
    openCallSheet:        stubAction('openCallSheet'),
    closeCallSheet:       stubAction('closeCallSheet'),
    fetchEnrichedContact: stubAction('fetchEnrichedContact'),
    toastVoicemail:       stubAction('toastVoicemail'),
    invalidateCallsQuery: stubAction('invalidateCallsQuery'),
    fadeButtonToValidate: stubAction('fadeButtonToValidate'),
    saveQualification:    stubAction('saveQualification'),
    hangupTwilio:         stubAction('hangupTwilio'),
    hangupVoicemail:      stubAction('hangupVoicemail'),
    logTransition:        stubAction('logTransition'),

    setBridgedAt: assign({
      bridgedAt: (_ctx, _params) => Date.now(),
    }),
    setVerdictSource: assign({
      verdictSource: (_ctx, params: { source: VerdictSource }) => params.source,
    }),
    setEnrichedContact: assign({
      enrichedContact: (_ctx, params: { contact: EnrichedContact }) => params.contact,
    }),
    setQualification: assign({
      outcome: (_ctx, params: { outcome: string; notes: string }) => params.outcome,
      notes: (_ctx, params: { outcome: string; notes: string }) => params.notes,
    }),
    markCallEnded: assign({
      enrichedContact: (ctx) =>
        ctx.context.enrichedContact ? { ...ctx.context.enrichedContact, callEnded: true } : null,
    }),
  },
}).createMachine({
  id: 'call',
  initial: 'idle',
  context: ({ input }) => ({
    callId: input.callId,
    prospectId: input.prospectId,
    phone: input.phone,
    enrichedContact: input.initialContact ?? null,
    bridgedAt: null,
    verdictSource: null,
    outcome: null,
    notes: '',
  }),
  on: {
    /** Enrichissement async du contact (ParallelCall.get + ProspectQueue fallback). */
    ENRICH_CONTACT: {
      actions: [
        {
          type: 'setEnrichedContact',
          params: ({ event }) => ({ contact: event.enrichedContact }),
        },
      ],
    },
    /** STOP_ALL est global — accepté depuis n'importe quel état non-final. */
    STOP_ALL: {
      target: '.completed',
      // hangupTwilio retire : handleStopAll du DialerContext s'en charge.
      // Etape 4 fusionnera les chemins (la machine deviendra seule responsable).
      actions: ['logTransition'],
    },
  },
  states: {
    idle: {
      on: {
        DIAL_START: {
          target: 'dialing',
          actions: ['playDialBeep', 'logTransition'],
        },
      },
    },

    dialing: {
      on: {
        TWILIO_RINGING: { target: 'ringing', actions: ['logTransition'] },
        HANGUP_REMOTE:  { target: 'completed', actions: ['logTransition'] },
      },
    },

    ringing: {
      on: {
        TWILIO_ANSWERED: {
          target: 'bridging',
          actions: [
            { type: 'setBridgedAt' },
            'logTransition',
          ],
        },
        HANGUP_REMOTE: { target: 'completed', actions: ['logTransition'] },
      },
    },

    bridging: {
      entry: [
        'fetchEnrichedContact',
        'openCallSheet',
        // #23 — "Supprimer le son parasite qui se produit parfois lors de la
        // connexion". Le bip de connexion partait a chaque entree dans
        // `bridging`, c'est-a-dire au moment exact que decrit le client. Le
        // port `playConnectionBeep` reste en place (il n'est plus appele) pour
        // que le retrait se lise comme une decision et non comme un oubli.
        'logTransition',
      ],
      on: {
        VOICEMAIL_DETECTED: {
          target: 'voicemail',
          actions: ['logTransition'],
        },
        HUMAN_CONFIRMED: {
          target: 'connected_to_agent',
          guard: 'isPoolFree',
          actions: [
            {
              type: 'setVerdictSource',
              params: ({ event }) => ({ source: event.verdictSource }),
            },
            'logTransition',
          ],
        },
        HANGUP_REMOTE: { target: 'completed', actions: ['logTransition'] },
      },
    },

    voicemail: {
      entry: [
        'toastVoicemail',
        'closeCallSheet',
        'hangupVoicemail',
        'invalidateCallsQuery',
        'logTransition',
      ],
      after: {
        /** AUTO_NEXT — laisse 300ms pour que le toast soit visible avant que le pool ne refill. */
        300: { target: 'completed' },
      },
    },

    connected_to_agent: {
      on: {
        HANGUP_REMOTE:      { target: 'qualified', actions: ['markCallEnded', 'logTransition'] },
        // hangupTwilio retire ici : handleStopAll/handleHangupAndContinue du
        // DialerContext s'en charge encore (etape 4 fusionnera les chemins).
        HANGUP_AGENT:       { target: 'qualified', actions: ['markCallEnded', 'logTransition'] },
        VOICEMAIL_DETECTED: { target: 'voicemail', actions: ['logTransition'] },
      },
    },

    qualified: {
      entry: ['fadeButtonToValidate', 'logTransition'],
      on: {
        QUALIFICATION_SAVED: {
          target: 'completed',
          actions: [
            {
              type: 'setQualification',
              params: ({ event }) => ({ outcome: event.outcome, notes: event.notes }),
            },
            // saveQualification retire : CallSheet onClick appelle deja
            // handleQualifyCall du DialerContext. Etape 4 fusionnera.
            'closeCallSheet',
            'invalidateCallsQuery',
            'logTransition',
          ],
        },
        SKIP_QUALIFICATION: {
          target: 'completed',
          actions: ['closeCallSheet', 'logTransition'],
        },
      },
    },

    completed: {
      type: 'final',
    },
  },
});

export type CallMachine = typeof callMachine;
